/**
 * Modal Manager
 * Initializes and manages different types of modals
 */

import { onDOMReady, devLog } from './utils.js';
import KidDonationModal from './kid-donation-modal.js';
import VideoPlayerModal from './video-player-modal.js';

class ModalManager {
    constructor() {
        this.modals = new Map();
        this.init();
    }

    init() {
        this.setupKidDonationModal();
        this.setupVideoPlayerModal();
        devLog('Modal Manager initialized');
    }

    setupKidDonationModal() {
        // Only initialize on kid pages with modal data
        if (typeof window.kidModalData === 'undefined' || !window.kidModalData.id) {
            return;
        }

        try {
            // Initialize the kid donation modal
            const donationModal = new KidDonationModal();
            this.modals.set('kidDonation', donationModal);

            // Attach to donate buttons
            this.attachKidDonateButtons(donationModal);

            devLog('Kid donation modal initialized for:', window.kidModalData.name);
        } catch (error) {
            console.error('Failed to initialize kid donation modal:', error);
        }
    }

    attachKidDonateButtons(donationModal) {
        const donateButtons = document.querySelectorAll('.kid-donate-button');
        
        if (donateButtons.length === 0) {
            devLog('No kid donate buttons found');
            return;
        }

        donateButtons.forEach((button, index) => {
            button.addEventListener('click', (e) => {
                e.preventDefault();
                
                try {
                    donationModal.openForKid(window.kidModalData);
                    
                    // Optional: Track analytics
                    if (typeof gtag !== 'undefined') {
                        gtag('event', 'donation_modal_opened', {
                            'event_category': 'Donation',
                            'event_label': window.kidModalData.name,
                            'kid_id': window.kidModalData.id
                        });
                    }
                    
                    devLog('Donation modal opened for:', window.kidModalData.name);
                } catch (error) {
                    console.error('Failed to open donation modal:', error);
                }
            });

            // Add visual feedback for successful attachment
            button.classList.add('modal-attached');
            button.setAttribute('data-modal-target', 'kidDonation');
            
            devLog(`Donate button ${index + 1} attached`);
        });
    }

    setupVideoPlayerModal() {
        // Initialize video modal for all pages with video triggers
        try {
            const videoModal = new VideoPlayerModal();
            this.modals.set('videoPlayer', videoModal);
            
            // Attach to video trigger buttons
            this.attachVideoTriggers(videoModal);
            
            devLog('Video player modal initialized');
        } catch (error) {
            console.error('Failed to initialize video player modal:', error);
        }
    }

    attachVideoTriggers(videoModal) {
        const videoTriggers = document.querySelectorAll('.video-modal-trigger');
        
        if (videoTriggers.length === 0) {
            devLog('No video modal triggers found');
            return;
        }

        videoTriggers.forEach((trigger, index) => {
            // Skip if already attached
            if (trigger.classList.contains('modal-attached')) {
                return;
            }

            trigger.addEventListener('click', (e) => {
                e.preventDefault();
                
                try {
                    const videoUrl = trigger.href;
                    const title = trigger.getAttribute('data-video-title') || 
                                 trigger.textContent.trim() || 
                                 'Video Player';
                    
                    if (!videoUrl) {
                        console.error('Video URL is required');
                        return;
                    }
                    
                    videoModal.openWithVideo(videoUrl, title);
                    
                    // Optional: Track analytics
                    if (typeof gtag !== 'undefined') {
                        gtag('event', 'video_modal_opened', {
                            'event_category': 'Video',
                            'event_label': title,
                            'video_url': videoUrl
                        });
                    }
                    
                    devLog('Video modal opened:', { url: videoUrl, title });
                } catch (error) {
                    console.error('Failed to open video modal:', error);
                }
            });

            // Add visual feedback for successful attachment
            trigger.classList.add('modal-attached');
            trigger.setAttribute('data-modal-target', 'videoPlayer');
            
            devLog(`Video trigger ${index + 1} attached:`, trigger.href);
        });
    }

    // Public method to get a modal instance
    getModal(type) {
        return this.modals.get(type);
    }

    // Public method to open a modal
    openModal(type, ...args) {
        const modal = this.modals.get(type);
        if (modal && typeof modal.open === 'function') {
            modal.open(...args);
        } else {
            console.error(`Modal type "${type}" not found or does not have an open method`);
        }
    }

    // Public method to close a modal
    closeModal(type) {
        const modal = this.modals.get(type);
        if (modal && typeof modal.close === 'function') {
            modal.close();
        } else {
            console.error(`Modal type "${type}" not found or does not have a close method`);
        }
    }

    // Public method to close all modals
    closeAllModals() {
        this.modals.forEach((modal, type) => {
            if (modal && typeof modal.close === 'function' && modal.isModalOpen) {
                modal.close();
                devLog(`Closed modal: ${type}`);
            }
        });
    }

    // Cleanup method
    destroy() {
        this.modals.forEach((modal, type) => {
            if (modal && typeof modal.destroy === 'function') {
                modal.destroy();
                devLog(`Destroyed modal: ${type}`);
            }
        });
        this.modals.clear();
    }
}

// Global modal manager instance
let globalModalManager = null;

// Initialize when DOM is ready
onDOMReady(() => {
    globalModalManager = new ModalManager();
    
    // Make it globally accessible for debugging
    if (typeof window !== 'undefined') {
        window.modalManager = globalModalManager;
    }
});

// Handle page unload cleanup
window.addEventListener('beforeunload', () => {
    if (globalModalManager) {
        globalModalManager.destroy();
    }
});

export default ModalManager;
